import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Calendar, CheckCircle2, AlertCircle, Loader2, ExternalLink, LogOut } from "lucide-react";
import InfoTooltip from "@/components/admin/InfoTooltip";

export default function AcuityConnect({ API, getAuthHeaders, eventId, category }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    sync_services: true, sync_clients: true,
    prefill_enabled: true, show_final_price: true
  });

  useEffect(() => {
    fetch(`${API}/dashboard/${eventId}/acuity-settings`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setSettings(data);
          setForm({
            sync_services: data.sync_services !== false,
            sync_clients: data.sync_clients !== false,
            prefill_enabled: data.prefill_enabled !== false,
            show_final_price: data.show_final_price !== false,
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [API, getAuthHeaders, eventId]);

  // Detect successful OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("acuity_success") === "true") {
      const biz = params.get("business") || "Acuity";
      toast.success(`Connected to ${biz}!`);
      setSettings(s => ({ ...s, connected: true }));
      // Clean URL params
      const url = new URL(window.location);
      url.searchParams.delete("acuity_success");
      url.searchParams.delete("business");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, []);

  const handleConnect = async () => {
    // Real OAuth flow: get auth URL from backend, redirect to Acuity
    setSaving(true);
    try {
      const res = await fetch(`${API}/acuity/auth-url?event_id=${eventId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.auth_url) {
          window.location.href = data.auth_url;
          return;
        }
      }
      const errData = await res.json().catch(() => ({}));
      toast.error(errData.detail || "Failed to start Acuity connection");
    } catch (e) {
      toast.error("Could not connect to Acuity. Please try again.");
    }
    setSaving(false);
  };

  const handleDisconnect = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/dashboard/${eventId}/acuity-settings`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ connected: false, acuity_user_id: "", acuity_api_key: "" })
      });
      if (res.ok) {
        toast.success("Disconnected from Acuity");
        setSettings(s => ({ ...s, connected: false }));
      }
    } catch { toast.error("Error"); }
    setSaving(false);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/dashboard/${eventId}/acuity-settings`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, connected: settings?.connected || false, acuity_user_id: settings?.acuity_user_id || "" })
      });
      toast.success("Settings saved");
    } catch { toast.error("Error"); }
    setSaving(false);
  };

  const connected = settings?.connected;
  const categoryLabel = (category || "").charAt(0).toUpperCase() + (category || "").slice(1);

  if (loading) return <div className="py-8 text-center text-gray-400 text-sm">Loading Acuity settings...</div>;

  return (
    <Card data-testid="acuity-connect">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${connected ? "bg-green-50" : "bg-gray-50"}`}>
              <Calendar className={`w-5 h-5 ${connected ? "text-green-600" : "text-gray-400"}`} />
            </div>
            <div>
              <h4 className="font-medium flex items-center gap-2">
                Acuity Scheduling
                <InfoTooltip
                  label="Acuity Scheduling"
                  description="OAuth integration that pulls Acuity appointment-types as canonical services and one-way imports clients into Phygital. Tokens are stored on the event document under settings.acuity_*."
                  source="db.events.settings.acuity_*"
                  where_synced={["Service Match Modal", "Client Sync Modal", "/api/acuity/sync"]}
                />
                {connected ? (
                  <Badge className="bg-green-50 text-green-700 border-0 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Connected</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-gray-500">Not Connected</Badge>
                )}
              </h4>
              <p className="text-xs text-gray-500">Sync {categoryLabel.toLowerCase()} services and client bookings with Acuity</p>
            </div>
          </div>
        </div>

        {/* Connection state */}
        {!connected ? (
          <div className="space-y-3">
            <div className="p-4 border-2 border-dashed border-gray-200 rounded-xl text-center">
              <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700">Connect your Acuity account</p>
              <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
                Sign in with your Acuity account to sync services, clients, and bookings automatically.
              </p>
              <Button onClick={handleConnect} disabled={saving} className="mt-4 gap-2" data-testid="acuity-sign-in-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                Sign in with Acuity
              </Button>
            </div>
            <div className="p-3 bg-gray-50 border rounded-lg">
              <p className="text-xs text-gray-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Need an Acuity account?</p>
              <p className="text-xs text-gray-500 mt-1">
                <a href="https://acuityscheduling.com" target="_blank" rel="noreferrer" className="text-blue-600 underline inline-flex items-center gap-1">
                  Start a free trial at acuityscheduling.com <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Sync options */}
            <div className="flex items-center justify-between p-2.5 border rounded-lg">
              <div><p className="text-sm font-medium">Sync Services</p><p className="text-xs text-gray-500">Import appointment types from Acuity</p></div>
              <Switch checked={form.sync_services} onCheckedChange={v => setForm(f => ({ ...f, sync_services: v }))} />
            </div>
            <div className="flex items-center justify-between p-2.5 border rounded-lg">
              <div><p className="text-sm font-medium">Sync Clients</p><p className="text-xs text-gray-500">Share client preference cards with Acuity</p></div>
              <Switch checked={form.sync_clients} onCheckedChange={v => setForm(f => ({ ...f, sync_clients: v }))} />
            </div>
            <div className="flex items-center justify-between p-2.5 border rounded-lg">
              <div><p className="text-sm font-medium">Pre-fill Bookings</p><p className="text-xs text-gray-500">Auto-fill name, email, service when booked via Acuity</p></div>
              <Switch checked={form.prefill_enabled} onCheckedChange={v => setForm(f => ({ ...f, prefill_enabled: v }))} />
            </div>
            <div className="flex items-center justify-between p-2.5 border rounded-lg">
              <div><p className="text-sm font-medium">Show Final Price</p><p className="text-xs text-gray-500">Display price to clients (hide if Acuity handles payment)</p></div>
              <Switch checked={form.show_final_price} onCheckedChange={v => setForm(f => ({ ...f, show_final_price: v }))} />
            </div>

            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs text-green-800 font-medium">Sync Pipeline Active</p>
              <p className="text-xs text-green-600 mt-1">Acuity booking → Preference card pre-filled → Client completes intake → Host sees full card</p>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveSettings} disabled={saving} className="flex-1" data-testid="save-acuity-btn">
                {saving ? "Saving..." : "Save Settings"}
              </Button>
              <Button variant="outline" onClick={handleDisconnect} className="text-red-600 border-red-200 gap-1">
                <LogOut className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
